import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import {
  MdOutlineCategory,
  MdOutlineFamilyRestroom,
  MdOutlineNightlife,
  MdOutlineRestaurant,
  MdOutlinePhotoCamera,
  MdOutlineDirectionsBoat,
  MdOutlineHiking,
  MdOutlineWaves,
  MdOutlineSelfImprovement,
  MdOutlineNaturePeople,
  MdOutlineSchool,
  MdOutlineLocationCity,
} from "react-icons/md";
import { FaMountain, FaUmbrellaBeach } from "react-icons/fa";
import { GiParachute } from "react-icons/gi";
import { FiChevronRight } from "react-icons/fi";

/* ---------------- Skeleton Loader ---------------- */
const CategorySkeleton = () => (
  <div className="flex flex-col items-center justify-center gap-3 w-[140px] h-[120px] border border-gray-200 rounded-xl bg-white shadow-sm animate-pulse">
    <div className="bg-gray-200 p-3 rounded-full w-12 h-12"></div>
    <div className="h-4 w-20 bg-gray-200 rounded"></div>
  </div>
);

/* ---------------- Category → Icon Map ---------------- */
const categoryIconMap = {
  Adventure: FaMountain,
  "Beach & Relaxation": FaUmbrellaBeach,
  "Boating & Sailing": MdOutlineDirectionsBoat,
  "City Tours": MdOutlineLocationCity,
  Cultural: MdOutlineNaturePeople,
  "Extreme Sports": GiParachute,
  "Family Activities": MdOutlineFamilyRestroom,
  "Food & Drinks": MdOutlineRestaurant,
  "Hiking & Trekking": MdOutlineHiking,
  Nightlife: MdOutlineNightlife,
  Photography: MdOutlinePhotoCamera,
  "Water Sports": MdOutlineWaves,
  Wellness: MdOutlineSelfImprovement,
  "Wildlife & Nature": MdOutlineNaturePeople,
  "Workshops & Classes": MdOutlineSchool,
};

/* ---------------- Main Component ---------------- */
export default function ExploreByCategoriesSecond({
  categories = [],
  isLoading = false,
  onCategoryClick,
}) {
  const swiperRef = useRef(null);

  const handleNext = () => {
    swiperRef.current?.slideNext();
  };

  return (
    <div className="container mx-auto px-2 lg:px-12 py-12 relative">
      <h2 className="text-center text-[28px] font-semibold text-gray-900">
        Explore By Categories
      </h2>
      <p className="text-center text-gray-500 mt-2">
        Qualify as judged by customers. Book at the ideal price!
      </p>

      <div className="mt-10 relative">
        {isLoading ? (
          <div className="flex justify-center gap-6">
            {[...Array(5)].map((_, i) => (
              <CategorySkeleton key={i} />
            ))}
          </div>
        ) : categories.length > 0 ? (
          <>
            <Swiper
              modules={[Pagination, Navigation]}
              spaceBetween={20}
              slidesPerView={2.5}
              pagination={{ clickable: true }}
              loop={categories.length > 7}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              breakpoints={{
                375: { slidesPerView: 2.5 },
                640: { slidesPerView: 3.5 },
                768: { slidesPerView: 4.5 },
                1024: { slidesPerView: 6 },
                1280: { slidesPerView: 7 },
              }}
              className="top-exp-slider pb-[45px] "
            >
              {categories.map((c) => {
                const Icon = categoryIconMap[c.name] || MdOutlineCategory;

                return (
                  <SwiperSlide key={c.id}>
                    <div
                      onClick={() => onCategoryClick?.(c.id)}
                      className="flex flex-col items-center justify-center gap-3 w-[140px] h-[120px] mx-auto border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition cursor-pointer"
                    >
                      <div className="bg-[#E8FAFF] p-3 rounded-full text-[#00C3CC]">
                        <Icon size={26} />
                      </div>

                      <span className="text-gray-700 font-medium text-center text-sm">
                        {c.name}
                      </span>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>

            {/* Custom Right Arrow */}
            {categories.length > 7 && (
              <button
                onClick={handleNext}
                className="
                  absolute right-0 top-1/2 -translate-y-1/2 -translate-x-4
                  z-10
                  h-12 w-12
                  flex items-center justify-center
                  bg-white
                  border border-[#E5E7EB]
                  rounded-full
                  shadow-lg
                  hover:bg-gray-50
                  hover:shadow-xl
                  transition-all
                  duration-300
                  group
                "
                aria-label="Next categories"
              >
                <FiChevronRight
                  size={24}
                  className="text-gray-700 group-hover:text-[#1FADC6]"
                />
              </button>
            )}
          </>
        ) : (
          <p className="text-gray-500 text-center">No categories available</p>
        )}
      </div>
    </div>
  );
}
